import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Undo2, Check, X, AlertTriangle, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AiPrivacyNotice } from "./AiPrivacyNotice";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEditor, findSection } from "./useEditorState";
import { errMsg } from "@/lib/api";
import CostButton from "@/components/ai/CostButton";
import StopButton from "@/components/ai/StopButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const SUGGEST = {
  section: ["Rendi questa sezione più elegante e minimalista", "Migliora la versione mobile di questa sezione", "Riscrivi i testi in modo più persuasivo"],
  page: ["Aggiungi una sezione con tre pacchetti", "Correggi il layout mobile della pagina", "Aggiungi una sezione FAQ in fondo"],
  project: ["Cambia il colore principale in beige", "Aggiungi una pagina Contatti", "Crea una simulazione di checkout"],
  component: ["Rendi il menu mobile più moderno", "Aggiungi un pulsante di prenotazione nel menu"],
};
const STATUS = { pending: "Da confermare", applied: "Applicata", discarded: "Scartata", undone: "Annullata", error: "Errore", applying: "Applicazione…", cancelled: "Interrotta" };

function OpsList({ m }) {
  const [open, setOpen] = useState(null);
  return (
    <ul className="mt-2 space-y-1" data-testid="ai-ops-list">
      {m.ops.map((op, i) => (
        <li key={i} className="rounded border bg-white">
          <button className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left text-[11.5px] text-slate-700" onClick={() => setOpen(open === i ? null : i)}>
            {open === i ? <ChevronDown size={12} /> : <ChevronRight size={12} />}<span className="truncate">{op.label}</span>
            {op.repaired && <span className="ml-auto shrink-0 text-[10px] text-emerald-700">corretta</span>}
          </button>
          {op.error && <p className="px-2 pb-1.5 text-[10.5px] text-red-600" data-testid={`chat-op-error-${i}`}>Non applicabile (validazione): {op.error}</p>}
          {open === i && <pre className="max-h-56 overflow-auto border-t bg-slate-50 p-2 text-[10.5px] font-mono whitespace-pre-wrap">{op.code || op.html || op.css || JSON.stringify(op.json || op.props || {}, null, 1)}</pre>}
        </li>
      ))}
    </ul>
  );
}

function Msg({ m, onApply, onDiscard, onUndo, busy, stop }) {
  if (m.role === "user") return <div className="ml-6 rounded-xl rounded-tr-sm bg-navy-900 text-white text-sm px-3.5 py-2.5 whitespace-pre-wrap" data-testid="chat-user-msg">{m.text}</div>;
  if (m.status === "running") {
    return (
      <div className="mr-6 space-y-2 px-1" data-testid="chat-running">
        <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin text-sky-600" />L'AI sta lavorando… (anche 1-2 minuti)</p>
        {stop}
      </div>
    );
  }
  return (
    <div className={`mr-6 rounded-xl rounded-tl-sm border px-3.5 py-2.5 text-sm ${m.status === "error" ? "bg-red-50 border-red-200 text-red-800" : m.status === "cancelled" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-white border-slate-200 text-slate-800"}`} data-testid="chat-assistant-msg">
      <p className="whitespace-pre-wrap">{["error", "cancelled"].includes(m.status) && <AlertTriangle size={13} className="inline mr-1" />}{m.text}</p>
      {m.ops?.length > 0 && (
        <>
          <div className="mt-2 flex items-center justify-between"><p className="text-[11px] uppercase tracking-wider text-slate-500">Elementi interessati ({m.ops.length})</p><span className="text-[10.5px] font-medium text-sky-700" data-testid="chat-msg-status">{STATUS[m.status]}</span></div>
          <OpsList m={m} />
        </>
      )}
      {m.ignored?.length > 0 && <p className="mt-2 text-[11px] text-amber-700">Ignorate perché fuori dall'ambito selezionato: {m.ignored.join("; ")}</p>}
      {m.status === "pending" && m.ops.some((o) => !o.error) && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={busy} onClick={() => onApply(m)} data-testid="chat-apply-btn"><Check size={14} className="mr-1" />Applica</Button>
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => onDiscard(m)} data-testid="chat-discard-btn"><X size={14} className="mr-1" />Scarta</Button>
        </div>
      )}
      {m.status === "applied" && <Button size="sm" variant="outline" className="mt-3 h-8" disabled={busy} onClick={() => onUndo(m)} data-testid="chat-undo-btn"><Undo2 size={14} className="mr-1" />Annulla modifica</Button>}
    </div>
  );
}

export default function ChatPanel() {
  const ed = useEditor();
  const { api, id, selection, content, pageId } = ed;
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [confirm, setConfirm] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scopeType, setScopeType] = useState(null);
  const list = useRef(null);
  const page = content.pages.find((p) => p.id === pageId);
  const sec = selection?.kind === "section" ? findSection(content, selection.id)?.section : null;
  const comp = selection?.kind === "component" ? content.components.find((c) => c.id === selection.id) : null;
  const options = [["project", "Intero progetto"], page && ["page", `Pagina «${page.name}»`], sec && ["section", `Sezione «${sec.name}»`], comp && ["component", `Componente «${comp.name}»`]].filter(Boolean);
  const def = sec ? "section" : comp ? "component" : "page";
  const st = options.some((o) => o[0] === scopeType) ? scopeType : def;
  useEffect(() => { setScopeType(null); }, [selection?.id]);

  const reload = useCallback(async () => { const { data } = await api.get(`/projects/${id}/chat`); setMsgs(data); return data; }, [api, id]);
  useEffect(() => { reload(); }, [reload]);
  const lastStatus = msgs[msgs.length - 1]?.status;
  useEffect(() => { list.current?.scrollTo({ top: list.current.scrollHeight, behavior: "smooth" }); }, [msgs.length, lastStatus]);

  const running = msgs.find((m) => m.status === "running" && m.job_id);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const { data: job } = await api.get(`/jobs/${running.job_id}`);
      if (job.status === "running") return;
      clearInterval(t);
      const data = await reload();
      const done = data.find((m) => m.id === running.id);
      if (done?.status === "applied") { const p = await ed.load(); ed.setProject((x) => ({ ...x, updated_at: p.updated_at })); toast.success("Modifica AI applicata e versione salvata"); }
      else if (done?.status === "pending") toast.message("Proposta pronta: controlla gli elementi e applica");
    }, 2000);
    return () => clearInterval(t);
  }, [running?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (msg, est) => {
    if (!msg.trim() || running) return;
    const scope = { type: st, page_id: page?.id, section_id: sec?.id, component_id: comp?.id };
    setBusy(true);
    try {
      await ed.save();
      const { data } = await api.post(`/projects/${id}/chat`, { message: msg, scope, auto_apply: !confirm, confirm_usd: est?.usd });
      setMsgs((xs) => [...xs, data.user, data.assistant]); setText("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const [force, setForce] = useState(null);
  const act = async (m, action, forced = false) => {
    setBusy(true);
    try {
      if (action !== "discard") await ed.save();
      const { data } = await api.post(`/projects/${id}/chat/${m.id}/${action}`, action === "undo" ? { force: forced } : undefined);
      if (data.content) { ed.replaceContent(data.content, { keepHistory: true }); ed.setProject((p) => ({ ...p, updated_at: new Date().toISOString() })); }
      await reload();
      toast.success({ apply: "Modifica applicata · versione precedente salvata", undo: "Modifica annullata", discard: "Proposta scartata" }[action]);
    } catch (e) {
      const msg = errMsg(e);
      if (action === "undo" && e?.response?.status === 409 && msg.startsWith("MODIFIED:")) setForce({ m, msg: msg.replace("MODIFIED: ", "") });
      else toast.error(msg);
    } finally { setBusy(false); }
  };
  const clear = async () => { if (window.confirm("Svuotare la cronologia della chat?")) { await api.delete(`/projects/${id}/chat`); reload(); } };

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="chat-panel">
      <div className="px-3 py-2.5 border-b space-y-2">
        <div className="flex items-center gap-2">
          <Select value={st} onValueChange={setScopeType}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-0" data-testid="chat-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={clear} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Svuota chat" data-testid="chat-clear-btn"><Trash2 size={14} /></button>
        </div>
        <label className="flex items-center justify-between text-[11px] text-slate-600">Mostra le modifiche prima di applicarle<Switch checked={confirm} onCheckedChange={setConfirm} data-testid="chat-confirm-switch" /></label>
      </div>
      <div ref={list} className="flex-1 min-h-0 overflow-auto p-3 space-y-3" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="text-center py-6">
            <Sparkles className="mx-auto text-sky-500" size={20} />
            <p className="mt-2 text-sm text-slate-600">Chiedi all'AI di modificare la demo.</p>
            <p className="mt-1 text-[11px] text-slate-500">Le modifiche restano nell'ambito scelto e ogni modifica salva una versione ripristinabile.</p>
          </div>
        )}
        {msgs.map((m) => <Msg key={m.id} m={m} busy={busy} stop={<StopButton api={api} projectId={id} testid="chat-stop-btn" />} onApply={(x) => act(x, "apply")} onDiscard={(x) => act(x, "discard")} onUndo={(x) => act(x, "undo")} />)}
      </div>
      <div className="border-t p-3 space-y-2">
        {!text && !running && <div className="flex flex-wrap gap-1.5">{(SUGGEST[st] || []).map((s) => <button key={s} onClick={() => setText(s)} className="text-[11px] px-2 py-1 rounded-full border text-slate-600 hover:border-sky-400">{s}</button>)}</div>}
        <AiPrivacyNotice testid="chat-ai-privacy-notice" />
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Es. Sposta il titolo in basso a sinistra e usa un'immagine a tutta larghezza"
          className="text-sm resize-none" data-testid="chat-input" />
        <CostButton path="/ai-edits/estimate" label="Invia" testid="chat-send-btn" busy={busy || !!running} disabled={!text.trim() || !!running}
          body={text.trim() ? { kind: "chat", scope_type: st, page_id: page?.id || "", section_id: sec?.id || "", component_id: comp?.id || "", instruction: text } : null}
          onRun={(est) => send(text, est)} />
      </div>
      <ConfirmDialog open={!!force} onOpenChange={(o) => !o && setForce(null)} title="Annullare comunque?" confirmLabel="Annulla comunque" destructive
        description={force?.msg} onConfirm={() => { const f = force; setForce(null); act(f.m, "undo", true); }} />
    </div>
  );
}
